import { getCommunicationsPage } from "@/api/client/communications";
import { getUser, updateUserCommunications } from "@/api/users/users-services";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import Loader from "@/components/utils/loader";
import { extractApiErrorMessage } from "@/lib/utils";
import { useStore } from "@/store";
import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";

const formSchema = z
  .object({
    name: z.string().min(1, { message: "Name is required" }),
    lastName: z.string().optional().or(z.literal("")),
    password: z
      .string()
      .min(8, { message: "Password must be at least 8 characters" })
      .regex(/[A-Z]/, {
        message: "Password must contain at least one uppercase letter",
      })
      .regex(/[a-z]/, {
        message: "Password must contain at least one lowercase letter",
      })
      .regex(/[0-9]/, { message: "Password must contain at least one number" })
      .regex(/[-_+!@#$%^&*(),.?":{}|<>]/, {
        message: "Password must contain at least one punctuation mark",
      })
      .optional()
      .or(z.literal("")),
    confirmPassword: z.string().optional().or(z.literal("")),
    roles: z
      .array(z.number())
      .min(1, { message: "At least one role is required" }),
    isActive: z.boolean().default(false),
    sendNotification: z.boolean().default(false),
    email: z
      .string()
      .email({ message: "Invalid email address" })
      .min(1, { message: "Email is required" }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  });

const EditUserForm = ({ onClose }) => {
  const { t } = useTranslation();
  const rolOptions = useStore((state) => state.rolOptions);
  const userToUpdateData = useStore((state) => state.userToUpdateData);
  const updateUser = useStore((state) => state.updateUser);
  const [loading, setLoading] = useState(false);
  const loggedInUser = useStore((state) => state.loggedInUser);
  const [isCommunicationsDialogOpen, setIsCommunicationsDialogOpen] =
    useState(false);
  const [communications, setCommunications] = useState([]);
  const [selectedCommunications, setSelectedCommunications] = useState([]);
  const [communicationsChanged, setCommunicationsChanged] = useState(false);
  const [initialCommunications, setInitialCommunications] = useState([]);
  const [communicationsSearch, setCommunicationsSearch] = useState("");
  const [appliedCommunicationsSearch, setAppliedCommunicationsSearch] = useState("");
  const [communicationsPage, setCommunicationsPage] = useState({complaint: 1, consultation: 1});
  const [communicationsHasMore, setCommunicationsHasMore] = useState({complaint: true, consultation: true});
  const [communicationsLoading, setCommunicationsLoading] = useState(false);
  const [communicationsTotal, setCommunicationsTotal] = useState({complaint: 0, consultation: 0});
  const communicationsRequestRef = useRef(0);
  const communicationsSelectionDirtyRef = useRef(false);

  const filteredRoles = useMemo(() => {
    if (!loggedInUser || !Array.isArray(loggedInUser.roles)) {
      return [];
    }
    const normalizedUserRoles = loggedInUser.roles.map((role) =>
      String(role).toLowerCase(),
    );
    const isSuperadmin = normalizedUserRoles.includes("superadmin");
    const isAdmin = normalizedUserRoles.includes("admin");
    const normalizeRoleName = (role) => String(role?.name ?? "").toLowerCase();

    if (isSuperadmin) {
      return rolOptions;
    }
    if (isAdmin) {
      return rolOptions.filter(
        (role) => normalizeRoleName(role) !== "superadmin",
      );
    }
    return [];
  }, [rolOptions, loggedInUser]);

  const canManageCommunications = useMemo(() => {
    if (!loggedInUser || !Array.isArray(loggedInUser.roles)) {
      return false;
    }
    const normalizedUserRoles = loggedInUser.roles.map((role) =>
      String(role).toLowerCase(),
    );
    return (
      normalizedUserRoles.includes("superadmin") ||
      normalizedUserRoles.includes("admin")
    );
  }, [loggedInUser]);

  const isUsereditOrUserview = useMemo(() => {
    if (!userToUpdateData || !Array.isArray(userToUpdateData.roles)) {
      return false;
    }
    const normalizedRoles = userToUpdateData.roles
      .map((role) => {
        if (typeof role === "object" && role.name) {
          return String(role.name).toLowerCase();
        }
        if (typeof role === "string") {
          return String(role).toLowerCase();
        }
        return "";
      })
      .filter((role) => role);

    return (
      normalizedRoles.includes("useredit") ||
      normalizedRoles.includes("userview")
    );
  }, [userToUpdateData]);

  const mergeRowsByDate = (rows) =>
    [...rows].sort((a, b) => new Date(b.date) - new Date(a.date));

  const fetchCommunicationsBatch = async (type, page, search) => {
    const response = await getCommunicationsPage({
      type,
      page,
      pageSize: 25,
      search,
    });
    const payload = response?.data || {};
    const rows = Array.isArray(payload.data) ? payload.data : [];
    const pagination = payload.pagination || {};
    return {
      rows,
      total: Number(pagination.total ?? rows.length) || 0,
      totalPages: Number(pagination.totalPages ?? 1) || 1,
    };
  };

  const fetchCommunications = async (reset = true, searchOverride, pageOverride, syncSelection = true) => {
    const requestId = ++communicationsRequestRef.current;
    try {
      const search = searchOverride !== undefined ? searchOverride : appliedCommunicationsSearch;
      const pages = pageOverride ?? communicationsPage;
      setCommunicationsLoading(true);
      const [complaintsBatch, consultationsBatch] = await Promise.all([
        pages.complaint > 0
          ? fetchCommunicationsBatch("complaint", pages.complaint, search)
          : Promise.resolve({rows: [], total: 0, totalPages: 1}),
        pages.consultation > 0
          ? fetchCommunicationsBatch("consultation", pages.consultation, search)
          : Promise.resolve({rows: [], total: 0, totalPages: 1}),
      ]);

      if (requestId !== communicationsRequestRef.current) {
        return;
      }

      setCommunicationsTotal({
        complaint: complaintsBatch.total,
        consultation: consultationsBatch.total,
      });
      setCommunicationsHasMore({
        complaint: pages.complaint < complaintsBatch.totalPages,
        consultation: pages.consultation < consultationsBatch.totalPages,
      });
      setCommunications((previous) => {
        const base = reset ? [] : previous;
        const seen = new Set(base.map((communication) => communication.id));
        const merged = [...base];
        for (const row of [...complaintsBatch.rows, ...consultationsBatch.rows]) {
          if (!seen.has(row.id)) {
            seen.add(row.id);
            merged.push(row);
          }
        }
        return mergeRowsByDate(merged);
      });

      if (reset && syncSelection && !communicationsSelectionDirtyRef.current) {
        const userData = await getUser(userToUpdateData.id);
        if (
          requestId !== communicationsRequestRef.current ||
          communicationsSelectionDirtyRef.current
        ) {
          return;
        }
        const userCommunications = userData?.communications || [];
        const userCommunicationIds = userCommunications.map((c) => c.id);

        setSelectedCommunications(userCommunicationIds);
        setInitialCommunications(userCommunicationIds);
      }
    } catch (error) {
      console.error("Error al obtener comunicaciones:", error);
      toast.error(t("alertDialog.toast.errorLoadingCommunications"));
    } finally {
      setCommunicationsLoading(false);
    }
  };
  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      lastName: "",
      password: "",
      confirmPassword: "",
      roles: [],
      isActive: false,
      sendNotification: false,
      email: "",
    },
  });

  useEffect(() => {
    if (userToUpdateData) {
      const userRoles = Array.isArray(userToUpdateData.roles)
        ? userToUpdateData.roles
            .map((role) => {
              if (typeof role === "number" && Number.isFinite(role)) {
                return filteredRoles.some((option) => option.id === role)
                  ? role
                  : undefined;
              }

              if (role && typeof role === "object") {
                const maybeId = role.id;
                if (typeof maybeId === "number" && Number.isFinite(maybeId)) {
                  return filteredRoles.some((option) => option.id === maybeId)
                    ? maybeId
                    : undefined;
                }
                if (typeof role.name === "string") {
                  return filteredRoles.find(
                    (option) =>
                      String(option?.name ?? "").toLowerCase() ===
                      role.name.toLowerCase(),
                  )?.id;
                }
              }

              const roleAsString = String(role ?? "").trim();
              if (!roleAsString) return undefined;

              const maybeId = Number(roleAsString);
              if (!Number.isNaN(maybeId) && Number.isFinite(maybeId)) {
                return filteredRoles.some((option) => option.id === maybeId)
                  ? maybeId
                  : undefined;
              }

              return filteredRoles.find(
                (option) =>
                  String(option?.name ?? "").toLowerCase() ===
                  roleAsString.toLowerCase(),
              )?.id;
            })
            .filter((id) => id !== undefined)
        : [];

      form.reset({
        name: userToUpdateData.name || "",
        lastName: userToUpdateData.lastName || "",
        password: "",
        confirmPassword: "",
        roles: userRoles,
        isActive: userToUpdateData.isActive || false,
        sendNotification: userToUpdateData.sendNotification || false,
        email: userToUpdateData.email || "",
      });
    }
  }, [userToUpdateData, form, filteredRoles]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setAppliedCommunicationsSearch(communicationsSearch);
    }, 400);
    return () => clearTimeout(timer);
  }, [communicationsSearch]);

  useEffect(() => {
    if (isCommunicationsDialogOpen && userToUpdateData?.id) {
      const firstPages = {complaint: 1, consultation: 1};
      setCommunicationsPage(firstPages);
      communicationsSelectionDirtyRef.current = false;
      fetchCommunications(true, appliedCommunicationsSearch, firstPages, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCommunicationsDialogOpen]);

  useEffect(() => {
    if (isCommunicationsDialogOpen && userToUpdateData?.id) {
      const firstPages = {complaint: 1, consultation: 1};
      setCommunicationsPage(firstPages);
      fetchCommunications(true, appliedCommunicationsSearch, firstPages, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedCommunicationsSearch]);

  const loadMoreCommunications = () => {
    const nextPages = {
      complaint: communicationsHasMore.complaint ? communicationsPage.complaint + 1 : 0,
      consultation: communicationsHasMore.consultation ? communicationsPage.consultation + 1 : 0,
    };
    if (nextPages.complaint === 0 && nextPages.consultation === 0) {
      return;
    }
    const mergedPages = {
      complaint: nextPages.complaint === 0 ? 0 : nextPages.complaint,
      consultation: nextPages.consultation === 0 ? 0 : nextPages.consultation,
    };
    setCommunicationsPage((previous) => ({
      complaint: nextPages.complaint === 0 ? previous.complaint : nextPages.complaint,
      consultation: nextPages.consultation === 0 ? previous.consultation : nextPages.consultation,
    }));
    fetchCommunications(false, appliedCommunicationsSearch, mergedPages);
  };

  useEffect(() => {
    const sortedSelected = [...selectedCommunications].sort();
    const sortedInitial = [...initialCommunications].sort();
    setCommunicationsChanged(
      JSON.stringify(sortedSelected) !== JSON.stringify(sortedInitial),
    );
  }, [selectedCommunications, initialCommunications]);

  const onSubmit = async (values) => {
    setLoading(true);
    const { confirmPassword, password, ...dataToSubmit } = values;
    if (password) {
      dataToSubmit.password = password;
    }
    try {
      await updateUser(userToUpdateData.id, dataToSubmit);

      if (communicationsChanged) {
        await updateUserCommunications(
          userToUpdateData.id,
          selectedCommunications,
        );
      }

      toast.success(t("alertDialog.toast.userUpdatedSuccess"));
      onClose();
    } catch (error) {
      console.error("Error al actualizar el usuario:", error);
      toast.error(
        extractApiErrorMessage(error, t("alertDialog.toast.userUpdatedError")),
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="w-full flex justify-center">
      <Card className="p-6 w-full bg-slate-50">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <h1 className="text-2xl font-light mb-4">
              {t("MainLayout.users.updateUsers")}
            </h1>
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem className="mb-4">
                  <FormLabel>{t("forms.myData.name")}</FormLabel>
                  <FormControl>
                    <Input placeholder={t("forms.myData.name")} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="sendNotification"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center space-x-2 mt-4 mb-4">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      id="sendNotification"
                    />
                  </FormControl>
                  <FormLabel
                    htmlFor="sendNotification"
                    className="text-sm font-medium leading-none cursor-pointer"
                  >
                    {t("forms.myData.sendNotification")}
                  </FormLabel>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem className="mb-4">
                  <FormLabel>{t("forms.myData.lastname")}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t("forms.myData.lastname")}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="roles"
              render={({ field }) => (
                <FormItem className="mb-4">
                  <FormLabel>{t("forms.myData.roles")}</FormLabel>
                  <div className="flex flex-wrap -mx-2">
                    {filteredRoles.map((option) => (
                      <div key={option.id} className="w-1/3 px-2 mb-2">
                        <FormItem className="flex flex-row items-start space-x-3 space-y-0 mt-1">
                          <FormControl>
                            <Checkbox
                              checked={field.value.includes(option.id)}
                              onCheckedChange={(checked) => {
                                const updatedRoles = checked
                                  ? [...field.value, option.id]
                                  : field.value.filter(
                                      (id) => id !== option.id,
                                    );
                                field.onChange(updatedRoles);
                              }}
                            />
                          </FormControl>
                          <FormLabel className="font-normal">
                            {option.name}
                          </FormLabel>
                        </FormItem>
                      </div>
                    ))}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem className="mb-4">
                  <FormLabel>{t("forms.myData.email")}</FormLabel>
                  <FormControl>
                    <Input placeholder={t("forms.myData.email")} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem className="mb-4">
                  <FormLabel>{t("forms.myData.password")}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t("forms.myData.password")}
                      type="password"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem className="mb-4">
                  <FormLabel>{t("forms.myData.confirmPassword")}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t("forms.myData.confirmPassword")}
                      type="password"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="isActive"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center space-x-2 mt-4 mb-4">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      id="isActive"
                    />
                  </FormControl>
                  <FormLabel
                    htmlFor="isActive"
                    className="text-sm font-medium leading-none cursor-pointer"
                  >
                    {t("forms.myData.activeUser")}
                  </FormLabel>
                  <FormMessage />
                </FormItem>
              )}
            />
            {canManageCommunications && isUsereditOrUserview && (
              <Button
                type="button"
                onClick={() => setIsCommunicationsDialogOpen(true)}
                className="mt-4 mb-4"
              >
                {t("buttonTexts.manageCommunications")}
              </Button>
            )}
            <Button
              type="submit"
              className="mt-4 bg-primary hover:bg-primary-600"
            >
              {loading ? (
                <Loader style={"buttonLoader"} />
              ) : (
                t("buttonTexts.saveChanges")
              )}
            </Button>
          </form>
        </Form>
      </Card>
      {isCommunicationsDialogOpen && (
        <Dialog
          open={isCommunicationsDialogOpen}
          onOpenChange={setIsCommunicationsDialogOpen}
        >
          <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {t("dialogTexts.manageCommunicationsTitle")}
              </DialogTitle>
            </DialogHeader>
            <div className="flex flex-col gap-2 mt-4">
              <Input
                placeholder={t("dialogTexts.searchCommunicationsPlaceholder")}
                value={communicationsSearch}
                onChange={(event) => setCommunicationsSearch(event.target.value)}
              />
              <p className="text-sm text-slate-500">
                {t("dialogTexts.communicationsVisibleCount", {
                  visible: communications.length,
                  total:
                    (communicationsTotal.complaint || 0) +
                    (communicationsTotal.consultation || 0),
                  selected: selectedCommunications.length,
                })}
              </p>
              {communications.map((communication) => (
                <div
                  key={communication.id}
                  className="flex items-center gap-2 p-2 border rounded hover:bg-slate-50"
                >
                  <Checkbox
                    checked={selectedCommunications.includes(communication.id)}
                    onCheckedChange={(checked) => {
                      communicationsSelectionDirtyRef.current = true;
                      if (checked) {
                        setSelectedCommunications([
                          ...selectedCommunications,
                          communication.id,
                        ]);
                      } else {
                        setSelectedCommunications(
                          selectedCommunications.filter(
                            (id) => id !== communication.id,
                          ),
                        );
                      }
                    }}
                    aria-label={`Seleccionar comunicación ${communication.identificationNumber}`}
                  />
                  <div className="flex flex-1 justify-between">
                    <div className="flex items-center gap-4">
                      <span className="font-medium">
                        #
                        {communication.identificationNumber || communication.id}
                      </span>
                      <span className="text-sm text-slate-500">
                        {communication.type === "complaint"
                          ? t("communicationTypes.complaint")
                          : t("communicationTypes.consultation")}
                      </span>
                    </div>
                    <div className="text-sm text-slate-500">
                      {communication.date
                        ? format(
                            new Date(communication.date),
                            "dd/MM/yyyy HH:mm",
                          )
                        : "-"}
                    </div>
                  </div>
                </div>
              ))}
              {communications.length === 0 && !communicationsLoading && (
                <p className="text-center text-slate-500">
                  {t("dialogTexts.noCommunications")}
                </p>
              )}
              {(communicationsHasMore.complaint || communicationsHasMore.consultation) && (
                <Button
                  type="button"
                  variant="outline"
                  disabled={communicationsLoading}
                  onClick={loadMoreCommunications}
                >
                  {communicationsLoading
                    ? t("dialogTexts.loadingCommunications")
                    : t("dialogTexts.loadMoreCommunications")}
                </Button>
              )}
            </div>
            <div className="flex justify-end gap-4 mt-4">
              <Button
                type="button"
                onClick={() => setIsCommunicationsDialogOpen(false)}
                variant="outline"
              >
                {t("buttonTexts.cancel")}
              </Button>
              <Button type="button" onClick={() => setIsCommunicationsDialogOpen(false)}>
                {t("buttonTexts.save")}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </section>
  );
};

export default EditUserForm;
